/**
 * Sales section layout — one route layout, one active sub-page.
 *
 * This layout renders no cross-section tabs. The active sub-route gets
 * its own page chrome (header eyebrow + H1 + descriptive subtitle) and
 * renders into the body via <Outlet/>.
 *
 * Depletions is the only sales surface — Distribution was retired at
 * HY's request. `/sales` redirects to `/sales/depletions`, and the old
 * `/sales/distribution` URL redirects there too so existing bookmarks
 * don't dead-end.
 *
 * Page chrome matches the AdminLayout pattern so the whole site uses
 * one heading system:
 *   - Section name lives in the global Header (next to the logo).
 *   - The body owns the page-specific H1 + subtitle.
 *   - No duplicate eyebrow on the body.
 */

import { Box, Container, Typography } from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { SiteLayout } from '../components/SiteLayout';
import { PageHeadingProvider } from './PageHeadingContext';

const INK = '#0f0a07';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

interface SectionMeta {
  /** Eyebrow shown next to the logo in the global Header. */
  headerLabel: string;
  /** H1 shown in the body. */
  title: string;
  /** Descriptive line under the H1. */
  description: string;
}

const SECTION_META: Record<'depletions', SectionMeta> = {
  depletions: {
    headerLabel: 'Depletions',
    title: 'Depletions',
    description:
      'Retail pull-through — state, account, and product depletions trends with monthly comparisons.',
  },
};

export function SalesDashboardPage() {
  // Depletions is the only section under /sales; every path that
  // reaches this layout resolves to it (see the redirects in App.tsx).
  const meta = SECTION_META.depletions;

  // Right-side slot — descendant views (e.g. DepletionsDashboardGuide)
  // push their dynamic metadata (date range, scope) into the heading
  // row instead of rendering a second context strip below.
  const [actions, setActions] = useState<ReactNode>(null);
  const api = useMemo(() => ({ setActions }), []);

  return (
    <PageHeadingProvider value={api}>
      <SiteLayout subtitle={meta.headerLabel} showDesktopHint>
        <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 5 } }}>
          {/* ── Page heading ──────────────────────────────────── */}
          <Box
            sx={{
              mb: { xs: 2.5, md: 3.5 },
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              justifyContent: 'space-between',
              gap: { xs: 1.5, md: 3 },
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: FONT_INTER,
                  fontWeight: 700,
                  fontSize: { xs: 24, md: 30 },
                  letterSpacing: '-0.015em',
                  lineHeight: 1.15,
                  color: INK,
                }}
              >
                {meta.title}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  fontFamily: FONT_INTER,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: 'rgba(15, 10, 7, 0.62)',
                  maxWidth: 720,
                }}
              >
                {meta.description}
              </Typography>
            </Box>
            {actions && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  // On md+ this row anchors to the bottom of the
                  // heading block so the chip aligns with the H1's
                  // baseline-area rather than floating above it.
                  pb: { md: 0.5 },
                }}
              >
                {actions}
              </Box>
            )}
          </Box>
        </Container>

        <Container maxWidth="xl" sx={{ pb: { xs: 4, md: 6 } }}>
          <Outlet />
        </Container>
      </SiteLayout>
    </PageHeadingProvider>
  );
}
